import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule,Validators } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatButtonToggle,MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDatepicker,MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatError,MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';

import { AnalyticsService } from '@shared/services/analytics.service';

interface PeriodOption {
  value: string;
  label: string;
}

interface RelativeOption {
  value: string;
  label: string;
}

export type ExportMode = 'period' | 'dateRange';

@Component({
    selector: 'app-kpi-export',
    templateUrl: './kpi-export.component.html',
    styleUrls: ['./kpi-export.component.scss'],
    standalone: true,
    imports: [
      ReactiveFormsModule,
      MatButtonToggleGroup,
      MatButtonToggle,
      MatFormField,
      MatInput,
      MatDatepickerInput,
      MatDatepickerToggle,
      MatSuffix,
      MatDatepicker,
      MatError,
      MatSelect,
      MatOption,
      MatProgressSpinner,
      MatButton,
      MatIcon,
    ],
})
export class KpiExportComponent {
  exportForm: FormGroup;
  isLoading = false;
  exportMode: ExportMode = 'dateRange';

  periodOptions: PeriodOption[] = [
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' },
  ];

  relativeOptions: RelativeOption[] = [
    { value: 'current', label: 'Current' },
    { value: 'previous', label: 'Previous' },
  ];

  constructor(
    private fb: FormBuilder,
    private analyticsService: AnalyticsService
  ) {
    this.exportForm = this.fb.group({
      period: ['year', Validators.required],
      relative: ['current', Validators.required],
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
    });

    // Set default date range to last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    this.exportForm.patchValue({
      startDate: thirtyDaysAgo,
      endDate: today,
    });

    this.updateValidators();
  }

  onExportModeChange(mode: ExportMode): void {
    this.exportMode = mode;
    this.updateValidators();
  }

  private updateValidators(): void {
    const periodControl = this.exportForm.get('period');
    const relativeControl = this.exportForm.get('relative');
    const startDateControl = this.exportForm.get('startDate');
    const endDateControl = this.exportForm.get('endDate');

    if (this.exportMode === 'period') {
      periodControl?.setValidators([Validators.required]);
      relativeControl?.setValidators([Validators.required]);
      startDateControl?.clearValidators();
      endDateControl?.clearValidators();
    } else {
      periodControl?.clearValidators();
      relativeControl?.clearValidators();
      startDateControl?.setValidators([Validators.required]);
      endDateControl?.setValidators([Validators.required]);
    }

    periodControl?.updateValueAndValidity();
    relativeControl?.updateValueAndValidity();
    startDateControl?.updateValueAndValidity();
    endDateControl?.updateValueAndValidity();
  }

  onExport(): void {
    if (this.exportForm.valid) {
      this.isLoading = true;

      let exportObservable;
      let fileName: string;

      if (this.exportMode === 'period') {
        const { period, relative } = this.exportForm.value;
        exportObservable = this.analyticsService.exportKpiReport(period, relative);
        fileName = this.generateFileNameFromPeriod(period, relative);
      } else {
        const { startDate, endDate } = this.exportForm.value;
        const startDateStr = this.formatDate(startDate);
        const endDateStr = this.formatDate(endDate);
        exportObservable = this.analyticsService.exportKpiReportByDateRange(
          startDateStr,
          endDateStr
        );
        fileName = this.generateFileNameFromDateRange(startDateStr, endDateStr);
      }

      exportObservable.subscribe({
        next: (blob: Blob) => {
          this.downloadFile(blob, fileName);
          this.isLoading = false;
        },
        error: error => {
          console.error('Error exporting KPI report:', error);
          this.isLoading = false;
        },
      });
    }
  }

  private formatDate(date: Date): string {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  }

  private downloadFile(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private generateFileNameFromPeriod(period: string, relative: string): string {
    const timestamp = new Date().toISOString().split('T')[0];
    return `kpi-report-${period}-${relative}-${timestamp}.csv`;
  }

  private generateFileNameFromDateRange(startDate: string, endDate: string): string {
    return `kpi-report-${startDate}-to-${endDate}.csv`;
  }

  get period() {
    return this.exportForm.get('period');
  }

  get relative() {
    return this.exportForm.get('relative');
  }

  get startDate() {
    return this.exportForm.get('startDate');
  }

  get endDate() {
    return this.exportForm.get('endDate');
  }
}
